// The top bar's search: documents by invoice number, supplier or amount.
//
// The query runs on the server (GET /search), inside the session's tenant
// context, so a result is only ever a document this person could already open.
// Nothing is searched in the browser: a list filtered here would be a page of
// one screen pretending to be the office.
//
// What it says is exactly what matched — "invoice number", "supplier",
// "amount" — and when the text was read as an amount, which amount, so a
// reviewer who typed "1.234" (two readings) can see it was not treated as one.

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { SearchResults } from '../api/types.ts';
import { format } from '../money.ts';
import { documentDisplay } from '../states.ts';
import { useWorkspace } from '../state/workspace.tsx';
import { Icon } from './Icon.tsx';
import { useT, type MessageKey } from '../i18n/index.tsx';

/** How long typing must pause before a search is sent. */
export const SEARCH_DEBOUNCE_MS = 250;
/** The API refuses shorter text; so does this box, without asking. */
export const SEARCH_MIN_CHARACTERS = 2;

const MATCHED_ON = new Set(['invoice_number', 'supplier', 'amount']);

type State =
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'done'; readonly body: SearchResults }
  | { readonly kind: 'error'; readonly message: string };

export function SearchBox({ onOpenDocument }: { readonly onOpenDocument: (documentId: string) => void }): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const tenantId = workspace.membership.tenantId;
  const token = workspace.token;
  const [text, setText] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  // ⌘K / Ctrl+K focuses the box, as the design's hint says.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const query = text.trim();
    if (query.length < SEARCH_MIN_CHARACTERS) {
      setState({ kind: 'idle' });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setState({ kind: 'searching' });
      void (async () => {
        try {
          const body = await request<SearchResults>(
            tenantPath(tenantId, `/search?q=${encodeURIComponent(query)}`),
            { token },
          );
          if (!cancelled) {
            setState({ kind: 'done', body });
            setActive(0);
          }
        } catch (caught) {
          if (!cancelled) {
            setState({
              kind: 'error',
              message: caught instanceof ApiError && caught.status === 0 ? t('search.unreachable') : t('search.failed'),
            });
          }
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, tenantId, token]);

  // Another office's results must not stay under this office's name.
  useEffect(() => {
    setText('');
    setState({ kind: 'idle' });
  }, [tenantId]);

  const results = state.kind === 'done' ? state.body.results : [];
  const choose = (documentId: string) => {
    setOpen(false);
    setText('');
    onOpenDocument(documentId);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(results.length - 1, current + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(0, current - 1));
    } else if (event.key === 'Enter') {
      const chosen = results[active];
      if (chosen !== undefined) choose(chosen.documentId);
    }
  };

  const showPanel = open && text.trim().length >= SEARCH_MIN_CHARACTERS && state.kind !== 'idle';
  const nameOf = (administrationId: string) =>
    workspace.administrations.find((administration) => administration.id === administrationId)?.name ?? t('search.administration');

  return (
    <div className="search-box">
      <Icon name="search" />
      <input
        ref={input}
        type="search"
        className="input"
        placeholder={t('search.placeholder')}
        aria-label={t('search.label')}
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      <span className="kbd" aria-hidden="true">
        ⌘K
      </span>
      {showPanel ? (
        <div className="search-results" id={listId} role="listbox" aria-label={t('search.results')}>
          {state.kind === 'searching' ? (
            <p className="card-body muted small">{t('search.searching')}</p>
          ) : state.kind === 'error' ? (
            <p className="card-body muted small" role="status">
              {state.message}
            </p>
          ) : results.length === 0 ? (
            <p className="card-body muted small" role="status">
              {t('search.none', { text: text.trim() })}
              {state.kind === 'done' && state.body.amount === null && /\d/.test(text) ? t('search.notAmount') : ''}
            </p>
          ) : (
            <>
              {state.kind === 'done' && state.body.amount !== null ? (
                <p className="card-body muted small" style={{ paddingBottom: 4 }}>
                  {t('search.alsoAmount', { amount: state.body.amount })}
                </p>
              ) : null}
              {results.map((result, index) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  key={result.documentId}
                  className={`row${index === active ? ' selected' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(result.documentId)}
                >
                  <div className="grow">
                    <strong>
                      {result.invoiceNumber ?? t('search.noInvoiceNumber')}
                      {result.supplierName === null ? '' : ` · ${result.supplierName}`}
                    </strong>
                    <p>
                      {nameOf(result.administrationId)} ·{' '}
                      {t('search.matchedOn', {
                        field: MATCHED_ON.has(result.matchedOn)
                          ? t(`search.field.${result.matchedOn}` as MessageKey)
                          : result.matchedOn,
                      })}{' '}
                      ·{' '}
                      {documentDisplay(result.state, result.booking).label}
                    </p>
                  </div>
                  {result.total === null ? null : <b>{format(result.total)}</b>}
                </button>
              ))}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
